import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../../lib/cn';

/** White card on the light canvas — the base surface of every CMS screen (Figma 54:3). */
export function Panel({ className, ...props }: ComponentProps<'section'>) {
  return (
    <section className={cn('rounded-[12px] border border-line bg-panel', className)} {...props} />
  );
}

export function PanelHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
      <div>
        <h2 className="text-[16px] font-semibold text-ink">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-[12px] text-muted">{subtitle}</p> : null}
      </div>
      {action}
    </header>
  );
}

/**
 * CMS/Badge: status, tier and audit-action chips. The label uses the `-ink` half of each pair —
 * on a 12–20 % fill the vivid token itself is 2.4–4.2:1 (QA 2026-09-15 UX-505).
 */
export const badgeVariants = cva(
  'inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-semibold',
  {
    variants: {
      tone: {
        neutral: 'bg-canvas text-muted',
        ok: 'bg-ok/12 text-ok-ink',
        warn: 'bg-warn/15 text-warn-ink',
        danger: 'bg-danger/12 text-danger-ink',
        pink: 'bg-pink/12 text-pink-ink',
        cyan: 'bg-cyan/20 text-cyan-ink',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type BadgeProps = ComponentProps<'span'> & VariantProps<typeof badgeVariants>;

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

/**
 * Colour of an audit action chip (Figma CMS-14): publishes green, deletes/closes red,
 * role/settings changes amber, everything else neutral pink-ish.
 */
export function auditTone(action: string): NonNullable<BadgeProps['tone']> {
  if (/\.(publish|create)$/.test(action)) return 'ok';
  if (/\.(delete|revoke|close|block)$/.test(action)) return 'danger';
  if (/role_change|settings\./.test(action)) return 'warn';
  return 'neutral';
}
